<template>
  <div class="property-editor">
    <FormField 
      :label="property.name"
      :description="property.description"
      :required="property.required"
    >
      <!-- String input -->
      <InputBox
        v-if="property.type === 'string' && !property.enum"
        :model-value="stringValue"
        :placeholder="property.default?.toString() || `Enter ${property.name}`"
        :readonly="isFieldReadonly"
        :show-lock-icon="showFieldLockIcon"
        @update:model-value="updateValue"
      />
      
      <!-- Enum/Select -->
      <SelectBox
        v-else-if="property.enum && property.enum.length > 0"
        :model-value="stringValue"
        :options="enumOptions"
        :placeholder="property.default?.toString() || `Select ${property.name}`"
        :disabled="isFieldReadonly"
        @update:model-value="updateValue"
      />
      
      <!-- Boolean toggle -->
      <Toggle
        v-else-if="property.type === 'boolean'"
        :model-value="booleanValue"
        :label="booleanValue ? 'Enabled' : 'Disabled'"
        :disabled="isFieldReadonly"
        @update:model-value="updateBooleanValue"
        @change="updateBooleanValue"
      />

      <SecretReferenceWidget
        v-else-if="property.type === 'secret' || isSecretValue"
        :model-value="secretValue"
        :readonly="isFieldReadonly"
        :vault-aliases="vaultAliases"
        @update:model-value="emit('update:modelValue', $event)"
      />
      
      <!-- Number input -->
      <InputBox
        v-else-if="property.type === 'number' || property.type === 'integer'"
        type="number"
        :model-value="numberValue"
        :placeholder="property.default?.toString() || `Enter ${property.name}`"
        :min="property.minimum"
        :max="property.maximum"
        :step="property.type === 'integer' ? 1 : undefined"
        :readonly="isFieldReadonly"
        :show-lock-icon="showFieldLockIcon"
        @update:model-value="updateValue"
      />
      
      <!-- Object - render nested properties -->
      <div v-else-if="property.type === 'object' || isObjectValue" class="nested-object">
        <div class="object-properties">
          <PropertyEditor
            v-for="(childValue, childKey) in objectValue"
            :key="childKey"
            :property="{
              name: String(childKey),
              type: getPropertyType(childValue),
              description: `Property: ${childKey}`,
              required: false
            }"
            :model-value="childValue"
            :readonly="isFieldReadonly"
            :nest-level="(nestLevel || 0) + 1"
            :path="fullPath"
            :vault-aliases="vaultAliases"
            @update:model-value="updateObjectProperty(String(childKey), $event)"
          />
        </div>
        <div v-if="!isFieldReadonly" class="object-actions">
          <Button 
            size="small" 
            variant="secondary"
            @click="showAddPropertyModal"
          >
            Add Property
          </Button>
        </div>
      </div>
      
      <!-- Array - render as JSON for now, will enhance later -->
      <div v-else-if="property.type === 'array'" class="array-editor">
        <InputBox
          :model-value="jsonValue"
          :placeholder="`Enter ${property.name} as JSON array`"
          :readonly="isFieldReadonly"
          :show-lock-icon="showFieldLockIcon"
          @update:model-value="updateJsonValue"
        />
      </div>
      
      <!-- Fallback to string input for unknown types (not string, enum, boolean, number, object, or array) -->
      <InputBox
        v-else-if="!['string', 'boolean', 'number', 'integer', 'object', 'array', 'secret'].includes(property.type) && !property.enum"
        :model-value="stringValue"
        :placeholder="property.default?.toString() || `Enter ${property.name}`"
        :readonly="isFieldReadonly"
        :show-lock-icon="showFieldLockIcon"
        @update:model-value="updateValue"
      />
    </FormField>
    
    <!-- Add Property Modal -->
    <AddPropertyModal
      ref="addPropertyModalRef"
      :existing-keys="Object.keys(objectValue)"
      :vault-aliases="vaultAliases"
      @add="onAddProperty"
      @cancel="onCancelAddProperty"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import FormField from '@/components/molecules/FormField.vue'
import InputBox from '@/components/atoms/InputBox.vue'
import SelectBox from '@/components/atoms/SelectBox.vue'
import Toggle from '@/components/atoms/Toggle.vue'
import Button from '@/components/atoms/Button.vue'
import { AddPropertyModal } from '@/components/molecules'
import SecretReferenceWidget from '@/components/molecules/SecretReferenceWidget.vue'
import { useFieldPermissions } from '@/composables/useFieldPermissions'
import { useConfigurationStore } from '@/stores/configuration'
import type { SecretReferenceValue } from '@/types/configuration'
import { buildSecretReference, isSecretReference } from '@/utils/secret-reference'

// Debouncing utility
function debounce<T extends (...args: any[]) => void>(func: T, delay: number): T {
  let timeoutId: ReturnType<typeof setTimeout>
  return ((...args: any[]) => {
    clearTimeout(timeoutId)
    timeoutId = setTimeout(() => func(...args), delay)
  }) as T
}

interface PropertySchema {
  name: string
  type: 'string' | 'number' | 'integer' | 'boolean' | 'array' | 'object' | 'secret'
  description?: string
  default?: any
  enum?: string[]
  minimum?: number
  maximum?: number
  required?: boolean
}

interface Props {
  property: PropertySchema
  modelValue: any
  readonly?: boolean
  nestLevel?: number
  path?: string // JSON path for field-level permissions
  vaultAliases?: string[]
}

interface Emits {
  (e: 'update:modelValue', value: any): void
}

const props = withDefaults(defineProps<Props>(), {
  readonly: false,
  nestLevel: 0,
  path: '',
  vaultAliases: () => []
})

const emit = defineEmits<Emits>()

// Initialize permission checking
const configStore = useConfigurationStore()
const { canEditField, shouldShowLockIcon } = useFieldPermissions()

// Compute the full path for this property
const fullPath = computed(() => {
  if (!props.path) return props.property.name
  return `${props.path}.${props.property.name}`
})

// Check if this specific field is editable
const isFieldReadonly = computed(() => {
  if (props.readonly) return true
  
  // Check field-level permissions for tenant configurations
  if (!configStore.editorState.selectedTenant) return true
  
  return !canEditField('tenants', fullPath.value)
})

// Check if we should show a lock icon
const showFieldLockIcon = computed(() => {
  if (!configStore.editorState.selectedTenant) return false
  
  return shouldShowLockIcon('tenants', fullPath.value)
})

const addPropertyModalRef = ref<InstanceType<typeof AddPropertyModal>>()

const stringValue = computed(() => {
  if (props.modelValue === null || props.modelValue === undefined) {
    return ''
  }
  // For objects, show JSON representation instead of [object Object]
  if (typeof props.modelValue === 'object') {
    return JSON.stringify(props.modelValue, null, 2)
  }
  return props.modelValue.toString()
})

const booleanValue = computed(() => {
  return Boolean(props.modelValue)
})

const numberValue = computed(() => {
  const num = Number(props.modelValue)
  return isNaN(num) ? 0 : num
})

const jsonValue = computed(() => {
  if (props.modelValue === null || props.modelValue === undefined) {
    return ''
  }
  if (typeof props.modelValue === 'object') {
    return JSON.stringify(props.modelValue, null, 2)
  }
  return props.modelValue.toString()
})

const isSecretValue = computed(() => isSecretReference(props.modelValue))

const secretValue = computed<SecretReferenceValue>(() => {
  if (isSecretReference(props.modelValue)) {
    return props.modelValue
  }

  return buildSecretReference({ ref: '' })
})

const enumOptions = computed(() => {
  if (!props.property.enum) return []
  return props.property.enum.map(value => ({
    value,
    label: value,
    description: undefined
  }))
})

// New computed properties for object handling
const isObjectValue = computed(() => {
  return (props.property.type === 'object') || 
         (props.modelValue && 
          typeof props.modelValue === 'object' && 
          !Array.isArray(props.modelValue) &&
          !(props.modelValue instanceof Date) &&
          !isSecretReference(props.modelValue))
})

const objectValue = computed(() => {
  if (isObjectValue.value && props.modelValue !== null && props.modelValue !== undefined) {
    return props.modelValue as Record<string, any>
  }
  return {}
})

// Helper function to determine property type from value
function getPropertyType(value: any): PropertySchema['type'] {
  if (value === null || value === undefined) return 'string'
  if (isSecretReference(value)) return 'secret'
  if (typeof value === 'boolean') return 'boolean'
  if (typeof value === 'number') {
    return Number.isInteger(value) ? 'integer' : 'number'
  }
  if (Array.isArray(value)) return 'array'
  if (typeof value === 'object') return 'object'
  return 'string'
}

function updateValue(value: string | number | boolean) {
  let convertedValue: any = value
  
  // Convert based on property type
  if (props.property.type === 'number' || props.property.type === 'integer') {
    convertedValue = Number(value)
    if (isNaN(convertedValue)) {
      convertedValue = props.property.default || 0
    }
  } else if (props.property.type === 'boolean') {
    convertedValue = Boolean(value)
  }
  
  // For string properties, use debounced update to reduce patch operations
  if (props.property.type === 'string') {
    debouncedStringUpdate(convertedValue)
  } else {
    emit('update:modelValue', convertedValue)
  }
}

// Debounced string update to reduce excessive patch operations
const debouncedStringUpdate = debounce((value: string) => {
  emit('update:modelValue', value)
}, 500) // 500ms delay

// Separate function for boolean updates to ensure proper handling
function updateBooleanValue(value: boolean) {
  emit('update:modelValue', value)
}

// Function to update object properties
function updateObjectProperty(key: string, value: any) {
  const currentObject = { ...objectValue.value }
  currentObject[key] = value
  
  emit('update:modelValue', currentObject)
}

// Function to show add property modal
function showAddPropertyModal() {
  // Use open(parentPath, existingKeys, isArray)
  const existingKeys = Object.keys(objectValue.value)
  addPropertyModalRef.value?.open(fullPath.value, existingKeys, false)
}

// Function to handle adding a new property (modal emits { key?, value, type, parentPath })
function onAddProperty(data: { key?: string; value: any; type: string; parentPath: string }) {
  if (!data.key) return; // require key for object add
  updateObjectProperty(data.key, data.value)
}

// Function to handle modal cancellation
function onCancelAddProperty() {
  // Modal will close automatically
}

function updateJsonValue(value: string | number) {
  const stringValue = value.toString()
  try {
    const parsed = JSON.parse(stringValue || '{}')
    emit('update:modelValue', parsed)
  } catch (error) {
    // Keep the string value if parsing fails
    emit('update:modelValue', stringValue)
  }
}
</script>

<style scoped lang="scss">
.property-editor {
  margin-bottom: 16px;
}

.nested-object {
  border-left: 2px solid var(--color-border);
  padding-left: 16px;
  margin-top: 8px;
}

.object-properties {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.object-actions {
  margin-top: 12px;
  padding-top: 8px;
  border-top: 1px solid var(--color-border-light);
}

.array-editor {
  .input-box {
    font-family: monospace;
    min-height: 80px;
  }
}

.debug-info {
  font-size: 10px;
  color: var(--vscode-descriptionForeground);
  background: var(--vscode-textCodeBlock-background);
  padding: 4px 8px;
  border-radius: 2px;
  margin-top: 4px;
  font-family: monospace;
  border-left: 2px solid var(--vscode-charts-orange);
}
</style>
